import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiSave, FiArrowLeft, FiUpload, FiCalendar, FiMapPin, FiLink, FiCheckCircle, FiInfo, FiClock, FiLayers, FiEye, FiX } from 'react-icons/fi';
import { createEvent, updateEvent, getEventById } from './eventService';
import { getAllGuilds } from '../guilds/guildService';
import { useAuth } from '../auth/AuthContext';
import {
    EVENT_TYPES,
    EVENT_MODES,
    EVENT_STATUS_DRAFT,
    EVENT_STATUS_PUBLISHED
} from '../shared/constants';
import '../doctors/DoctorList.css';

export default function EventForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const isEditMode = !!id;

    const [guilds, setGuilds] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const [formData, setFormData] = useState({
        title: '',
        description: '',
        type: EVENT_TYPES[0],
        date: '',
        startTime: '',
        endTime: '',
        mode: EVENT_MODES[0],
        meetingLink: '',
        venue: '',
        targetGuildIds: [],
        status: EVENT_STATUS_DRAFT,
        createdBy: currentUser?.uid || '',
    });

    const [bannerFile, setBannerFile] = useState(null);
    const [bannerPreview, setBannerPreview] = useState(null);
    const [allGuildsSelected, setAllGuildsSelected] = useState(false);

    useEffect(() => {
        loadGuilds();
        if (isEditMode) {
            loadEvent();
        }
    }, [id, isEditMode, loadEvent]);

    async function loadGuilds() {
        try {
            const guildsData = await getAllGuilds();
            setGuilds(guildsData);
        } catch (err) {
            console.error('Error loading guilds:', err);
        }
    }

    async function loadEvent() {
        try {
            setError('');
            setLoading(true);
            const event = await getEventById(id);
            if (event) {
                // Convert Firestore timestamp to date string
                const dateStr = event.date?.toDate
                    ? event.date.toDate().toISOString().split('T')[0]
                    : '';

                setFormData({
                    ...event,
                    date: dateStr,
                });

                if (event.bannerUrl) {
                    setBannerPreview(event.bannerUrl);
                }

                if (event.targetGuildIds?.includes('ALL')) {
                    setAllGuildsSelected(true);
                }
            }
        } catch (err) {
            setError('Failed to load event');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }

    function handleChange(e) {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    }

    function handleGuildToggle(guildId) {
        setFormData(prev => {
            const currentIds = prev.targetGuildIds || [];
            const newIds = currentIds.includes(guildId)
                ? currentIds.filter(id => id !== guildId)
                : [...currentIds, guildId];
            return { ...prev, targetGuildIds: newIds };
        });
    }

    function handleAllGuildsToggle() {
        setAllGuildsSelected(!allGuildsSelected);
        if (!allGuildsSelected) {
            setFormData(prev => ({ ...prev, targetGuildIds: ['ALL'] }));
        } else {
            setFormData(prev => ({ ...prev, targetGuildIds: [] }));
        }
    }

    function handleBannerChange(e) {
        const file = e.target.files[0];
        if (file) {
            setBannerFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setBannerPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    }

    async function handleSubmit(e) {
        e.preventDefault();
        setError('');

        // Validation
        if (!formData.title.trim()) {
            setError('Title is required');
            return;
        }

        if (!formData.date) {
            setError('Date is required');
            return;
        }

        if (!formData.targetGuildIds || formData.targetGuildIds.length === 0) {
            setError('Please select at least one guild or "All Guilds"');
            return;
        }

        if (formData.mode === 'Online' && !formData.meetingLink.trim()) {
            setError('Meeting link is required for online events');
            return;
        }

        if (formData.mode === 'Offline' && !formData.venue.trim()) {
            setError('Venue is required for offline events');
            return;
        }

        if (formData.date && formData.time) {
            const selectedDateTime = new Date(`${formData.date}T${formData.time}`);
            if (selectedDateTime < new Date()) {
                setError('Cannot schedule an event for a past time.');
                return;
            }
        }

        try {
            setLoading(true);

            // Convert date string to Firestore timestamp
            const eventData = {
                ...formData,
                date: new Date(formData.date),
            };

            if (isEditMode) {
                await updateEvent(id, eventData, bannerFile);
            } else {
                await createEvent(eventData, bannerFile);
            }

            navigate('/dashboard/events');
        } catch (err) {
            setError('Failed to save event');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="page-container">
            <div className="page-header">
                <div className="header-left">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '24px', marginBottom: '8px' }}>
                        <button
                            type="button"
                            onClick={() => navigate('/dashboard/events')}
                            className="btn-back-arrow"
                            style={{
                                background: '#f1f5f9',
                                border: 'none',
                                padding: '10px 20px',
                                borderRadius: '12px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                color: '#475569',
                                fontWeight: '600',
                                transition: 'all 0.2s ease'
                            }}
                        >
                            <FiArrowLeft size={20} />
                            <span>Back</span>
                        </button>
                        <h1 style={{ margin: 0 }}>{isEditMode ? 'Edit Event' : 'Create Event'}</h1>
                    </div>
                    <p className="subtitle" style={{ marginLeft: '110px' }}>{isEditMode ? 'Update existing event details' : 'Schedule a new event for your guild members'}</p>
                </div>
            </div>

            {error && (
                <div className="error-message">
                    <FiCheckCircle style={{ color: '#C62828' }} />
                    {error}
                </div>
            )}

            <form onSubmit={handleSubmit} className="form-container-v2">
                {/* 1. Basic Information */}
                <div className="form-card">
                    <div className="form-card-header">
                        <FiInfo className="header-icon" />
                        <h3>Basic Information</h3>
                    </div>

                    <div className="form-group">
                        <label>Event Title *</label>
                        <div className="field-icon-wrapper">
                            <FiLayers className="field-icon" />
                            <input
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleChange}
                                placeholder="e.g., Annual Medical Conference 2026"
                                className="form-input-v2"
                                required
                            />
                        </div>
                    </div>

                    <div className="form-group" style={{ marginTop: '20px' }}>
                        <label>Description</label>
                        <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            placeholder="Provide details about the event, agenda, or speakers..."
                            rows="4"
                            style={{ paddingLeft: '16px' }}
                            className="form-input-v2"
                        />
                    </div>

                    <div className="form-row" style={{ marginTop: '20px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                        <div className="form-group">
                            <label>Event Type *</label>
                            <div className="field-icon-wrapper">
                                <FiCheckCircle className="field-icon" />
                                <select
                                    name="type"
                                    value={formData.type}
                                    onChange={handleChange}
                                    className="form-select-v2"
                                    required
                                >
                                    {EVENT_TYPES.map(type => (
                                        <option key={type} value={type}>{type}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Event Mode *</label>
                            <div className="field-icon-wrapper">
                                <FiEye className="field-icon" />
                                <select
                                    name="mode"
                                    value={formData.mode}
                                    onChange={handleChange}
                                    className="form-select-v2"
                                    required
                                >
                                    {EVENT_MODES.map(mode => (
                                        <option key={mode} value={mode}>{mode}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 2. Schedule */}
                <div className="form-card">
                    <div className="form-card-header">
                        <FiCalendar className="header-icon" />
                        <h3>Schedule</h3>
                    </div>

                    <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
                        <div className="form-group">
                            <label>Date *</label>
                            <div className="field-icon-wrapper">
                                <FiCalendar className="field-icon" />
                                <input
                                    type="date"
                                    name="date"
                                    value={formData.date}
                                    min={new Date().toISOString().split('T')[0]}
                                    onChange={handleChange}
                                    className="form-input-v2"
                                    required
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Start Time *</label>
                            <div className="field-icon-wrapper">
                                <FiClock className="field-icon" />
                                <input
                                    type="time"
                                    name="startTime"
                                    value={formData.startTime}
                                    onChange={handleChange}
                                    className="form-input-v2"
                                    required
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label>End Time *</label>
                            <div className="field-icon-wrapper">
                                <FiClock className="field-icon" />
                                <input
                                    type="time"
                                    name="endTime"
                                    value={formData.endTime}
                                    onChange={handleChange}
                                    className="form-input-v2"
                                    required
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* 3. Location / Link */}
                {(formData.mode === 'Online' || formData.mode === 'Offline') && (
                    <div className="form-card">
                        <div className="form-card-header">
                            {formData.mode === 'Online' ? <FiLink className="header-icon" /> : <FiMapPin className="header-icon" />}
                            <h3>{formData.mode === 'Online' ? 'Meeting Details' : 'Venue Details'}</h3>
                        </div>

                        {formData.mode === 'Online' ? (
                            <div className="form-group">
                                <label>Meeting Link *</label>
                                <div className="field-icon-wrapper">
                                    <FiLink className="field-icon" />
                                    <input
                                        type="url"
                                        name="meetingLink"
                                        value={formData.meetingLink}
                                        onChange={handleChange}
                                        placeholder="https://zoom.us/j/123456789"
                                        className="form-input-v2"
                                    />
                                </div>
                            </div>
                        ) : (
                            <div className="form-group">
                                <label>Venue Address *</label>
                                <div className="field-icon-wrapper">
                                    <FiMapPin className="field-icon" />
                                    <input
                                        type="text"
                                        name="venue"
                                        value={formData.venue}
                                        onChange={handleChange}
                                        placeholder="e.g., Grand Ballroom, City Hotel, Bangalore"
                                        className="form-input-v2"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* 4. Visibility & Target */}
                <div className="form-card">
                    <div className="form-card-header">
                        <FiLayers className="header-icon" />
                        <h3>Target Audience (Guilds) *</h3>
                    </div>

                    <div className="guild-selection-grid">
                        <label className={`guild-chip-label ${allGuildsSelected ? 'selected' : ''}`}>
                            <input
                                type="checkbox"
                                checked={allGuildsSelected}
                                onChange={handleAllGuildsToggle}
                            />
                            <FiCheckCircle size={16} />
                            <span>ALL GUILDS</span>
                        </label>

                        {!allGuildsSelected && guilds.map(guild => (
                            <label
                                key={guild.id}
                                className={`guild-chip-label ${formData.targetGuildIds?.includes(guild.id) ? 'selected' : ''}`}
                            >
                                <input
                                    type="checkbox"
                                    checked={formData.targetGuildIds?.includes(guild.id)}
                                    onChange={() => handleGuildToggle(guild.id)}
                                />
                                {formData.targetGuildIds?.includes(guild.id) && <FiCheckCircle size={14} />}
                                <span>{guild.name}</span>
                            </label>
                        ))}
                    </div>
                </div>

                {/* 5. Media & Publication */}
                <div className="form-card">
                    <div className="form-card-header">
                        <FiUpload className="header-icon" />
                        <h3>Media & Publication</h3>
                    </div>

                    <div className="form-group">
                        <label>Banner Image (Recommended 1200x400)</label>
                        <div className="banner-upload-wrapper" onClick={() => document.getElementById('bannerInput').click()}>
                            {bannerPreview ? (
                                <img src={bannerPreview} alt="Banner Preview" className="banner-preview-img" />
                            ) : (
                                <div className="upload-placeholder">
                                    <FiUpload size={40} style={{ color: '#94a3b8', marginBottom: '12px' }} />
                                    <p style={{ color: '#64748b', fontWeight: 500 }}>Click to upload event banner</p>
                                    <small style={{ color: '#94a3b8' }}>PNG, JPG or WebP supported</small>
                                </div>
                            )}
                            <input
                                id="bannerInput"
                                type="file"
                                accept="image/*"
                                onChange={handleBannerChange}
                                style={{ display: 'none' }}
                            />
                        </div>
                    </div>

                    <div className="form-group" style={{ marginTop: '24px' }}>
                        <label>Publication Status</label>
                        <div className="field-icon-wrapper">
                            <FiCheckCircle className="field-icon" />
                            <select
                                name="status"
                                value={formData.status}
                                onChange={handleChange}
                                className="form-select-v2"
                            >
                                <option value={EVENT_STATUS_DRAFT}>Stay in Draft</option>
                                <option value={EVENT_STATUS_PUBLISHED}>Publish Immediately</option>
                            </select>
                        </div>
                    </div>
                </div>

                <div className="form-actions" style={{ justifyContent: 'flex-end', marginTop: '12px' }}>
                    <button
                        type="button"
                        onClick={() => navigate('/dashboard/events')}
                        className="btn-secondary"
                        style={{ padding: '12px 24px', borderRadius: '12px' }}
                    >
                        <FiX />
                        <span>Discard Changes</span>
                    </button>
                    <button
                        type="submit"
                        className="btn-primary"
                        disabled={loading}
                        style={{ padding: '12px 32px', borderRadius: '12px' }}
                    >
                        <FiSave />
                        <span>{loading ? 'Saving...' : (isEditMode ? 'Update Event' : 'Create Event')}</span>
                    </button>
                </div>
            </form>
        </div>
    );
}
